import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import { Input } from 'ui-components/components/input';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import FormattedCurrency from '../../../../../../components/formatted-currency';

import { ActionsColumn } from './columns/actions-column';
import { CurrencyColumn } from './columns/currency-column';
import { EachesColumn } from './columns/eaches-column';
import { InboundColumn } from './columns/inbound-column';
import { ProductColumn } from './columns/product-column';
import { UomSelectColumn } from './columns/uom-select-column';
import styles from './columns.styles.module.less';
import { messages } from './messages';
import { purchaseOrderProductProperties } from '../../../data/purchase-order-properties';
import { computeAbsoluteEaches } from '../../../../../../constants/units-of-measure';

/**
 * @module columns
 * @description Generates column definitions for the product grid table in the purchase order drawer.
 * @param {(productId: string|number, field: string, value: any) => void} handleUpdateProduct - Function to update a product's field
 * @param {(productId: string|number) => void} handleRemoveProduct - Function to remove a product from the purchase order
 * @param {(productId: string|number) => void} openProductDetails - Function to open product details modal
 * @param {boolean} isReadOnly - Whether the grid should be in read-only mode
 * @returns {Array<object>} An array of column definitions compatible with react-table
 * Each column definition includes:
 * - Header: The column header (using FormattedMessage for internationalization)
 * - accessor: The property key to access data from the row
 * - Cell: A render function for the cell content
 * - Additional properties like width, className, etc.
 * @example
 * const columns = getColumns(
 *   (productId, field, value) => updateProduct(productId, field, value),
 *   (productId) => removeProduct(productId),
 *   (productId) => openDetails(productId),
 *   false
 * );
 */
export const getColumns = (
  handleUpdateProduct,
  handleRemoveProduct,
  openProductDetails,
  isReadOnly = false,
  currencyCode,
  isUnitsOfMeasureEnabled = false,
  isInboundLoadingRef,
  isInboundEnabled = false,
) => [
  {
    Header: <FormattedMessage {...messages.product} />,
    accessor: 'product',
    isSortable: true,
    Cell: ({ row }) => <ProductColumn data={row.original} openProductDetails={openProductDetails} />,
    className: styles['product-column-header'],
    width: 200,
  },
  {
    Header: <FormattedMessage {...messages.supplierSKU} />,
    accessor: purchaseOrderProductProperties.supplierSku,
    Cell: ({ row }) =>
      isReadOnly ? (
        <Text textType={TextType.Paragraph} value={row.original.supplierSku || ''} />
      ) : (
        <Input
          onChange={(value) =>
            handleUpdateProduct(row.original.rowKey ?? `${row.original.productId}`, purchaseOrderProductProperties.supplierSku, value)
          }
          value={row.original.supplierSku}
        />
      ),
    width: 200,
  },
  ...(isUnitsOfMeasureEnabled
    ? [
        {
          Header: <FormattedMessage {...messages.unitOfMeasure} />,
          accessor: purchaseOrderProductProperties.unitOfMeasure,
          Cell: ({ row, rows }) =>
            isReadOnly ? (
              <Text
                textType={TextType.Paragraph}
                value={row.original.unitsOfMeasure?.find((u) => u.productUnitOfMeasureId === row.original.unitOfMeasure)?.uomTypeName ?? ''}
              />
            ) : (
              <UomSelectColumn
                data={row.original}
                disabledUomIds={rows
                  .filter(
                    (r) =>
                      r.original.productId === row.original.productId &&
                      r.original.rowKey !== row.original.rowKey &&
                      r.original.unitOfMeasure != null,
                  )
                  .map((r) => r.original.unitOfMeasure)}
                handleUpdateProduct={handleUpdateProduct}
              />
            ),
        },
      ]
    : []),
  {
    Header: <FormattedMessage {...messages.qty} />,
    accessor: purchaseOrderProductProperties.quantity,
    Cell: ({ row }) =>
      isReadOnly ? (
        <Text textType={TextType.Paragraph} value={row.original.quantity?.toString() || '0'} />
      ) : (
        <NumberInput
          min={1}
          onChange={(value) =>
            handleUpdateProduct(
              row.original.rowKey ?? `${row.original.productId}`,
              purchaseOrderProductProperties.quantity,
              parseInt(value, 10) || 0,
            )
          }
          value={row.original.quantity}
        />
      ),
  },
  ...(isUnitsOfMeasureEnabled
    ? [
        {
          Header: <FormattedMessage {...messages.eaches} />,
          accessor: 'eaches',
          width: 70,
          Cell: ({ row }) => <EachesColumn data={row.original} />,
        },
      ]
    : []),
  ...(isInboundEnabled
    ? [
        {
          Header: (
            <Tooltip position='bottom'>
              <TooltipContent>
                <div className={styles['tooltip-text']}>
                  <FormattedMessage {...messages.inboundTooltip} />
                </div>
              </TooltipContent>
              <div className={styles['inbound-header']}>
                <FormattedMessage {...messages.inbound} />
                <FontAwesomeIcon className={styles['info-icon']} icon={faInfoCircle} />
              </div>
            </Tooltip>
          ),
          accessor: 'inboundQuantity',
          width: 100,
          Cell: ({ row }) => <InboundColumn inboundQuantity={row.original.inboundQuantity} isLoading={isInboundLoadingRef.current} />,
        },
      ]
    : []),
  {
    Header: <FormattedMessage {...messages.costPerItem} />,
    accessor: 'cost',
    width: 130,
    Cell: ({ row }) =>
      isReadOnly ? (
        <Text textType={TextType.Paragraph} value={row.original.cost?.toString() || '0'} />
      ) : (
        <CurrencyColumn currencyCode={currencyCode} data={row.original} handleUpdateProduct={handleUpdateProduct} />
      ),
  },
  {
    Header: <FormattedMessage {...messages.total} />,
    accessor: 'total',
    className: styles['total-cost-cell'],
    width: 100,
    Cell: ({ row }) => {
      const qty = row.original.quantity ?? 0;
      const cost = row.original.cost ?? 0;
      const absoluteEaches =
        isUnitsOfMeasureEnabled && (row.original.unitOfMeasure ?? 0) > 0
          ? computeAbsoluteEaches(row.original.unitOfMeasure, row.original.unitsOfMeasure ?? [])
          : 1;
      return (
        <div className={styles['total-cost-column']}>
          <Text
            textType={TextType.Paragraph}
            value={<FormattedCurrency currencyCode={currencyCode} value={qty * cost * absoluteEaches} />}
          />
        </div>
      );
    },
  },
  {
    Header: <FormattedMessage {...messages.actions} />,
    accessor: 'actions',
    width: 80,
    Cell: ({ row }) =>
      !isReadOnly && (
        <ActionsColumn handleRemoveProduct={handleRemoveProduct} productId={row.original.rowKey ?? `${row.original.productId}`} />
      ),
    className: styles['justify-center'],
  },
];
